import React, { useMemo, useState } from 'react';
import { Facebook, Plus, Trash2, ExternalLink, Copy, Share2, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useFacebookGroups } from '../hooks/useFacebookGroups';
import { toast } from '../hooks/use-toast';

export default function FacebookShareSection({ collection, totalSprites, displayName }) {
  const { groups, addGroup, removeGroup } = useFacebookGroups();
  const [selectedId, setSelectedId] = useState(groups[0]?.id || '');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const selected = useMemo(
    () => groups.find((g) => g.id === selectedId) || groups[0],
    [groups, selectedId]
  );

  const ownedCount = useMemo(() => {
    if (!collection) return 0;
    return Object.values(collection).filter((e) => e?.owned).length;
  }, [collection]);
  const masteredCount = useMemo(() => {
    if (!collection) return 0;
    return Object.values(collection).filter((e) => e?.mastered).length;
  }, [collection]);

  const buildShareText = () => {
    const who = displayName?.trim() ? displayName.trim() : 'A sprite collector';
    return (
      `🦙 ${who}'s Fortnite Sprite Vault\n` +
      `🎯 Owned: ${ownedCount}/${totalSprites}\n` +
      `⭐ Mastered: ${masteredCount}\n` +
      `Looking to trade & borrow sprites — drop a comment!\n` +
      `${window.location.href}`
    );
  };

  const handleOpenGroup = () => {
    if (!selected) {
      toast({ title: 'Pick a group', description: 'Choose a Facebook group from the dropdown first.' });
      return;
    }
    const text = buildShareText();
    try {
      navigator.clipboard?.writeText(text);
      toast({ title: 'Share text copied!', description: 'Paste it into the group post.' });
    } catch { /* ignore */ }
    window.open(selected.url, '_blank', 'noopener,noreferrer');
  };

  const handleFacebookShareDialog = () => {
    const url = window.location.href;
    const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(buildShareText())}`;
    window.open(fbShare, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const handleCopyOnly = () => {
    try {
      navigator.clipboard?.writeText(buildShareText());
      toast({ title: 'Copied!', description: 'Share text copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Clipboard is unavailable in this browser.' });
    }
  };

  const handleAdd = () => {
    if (!newName.trim() || !newUrl.trim()) {
      toast({ title: 'Missing info', description: 'Enter both a name and a Facebook group URL.' });
      return;
    }
    const item = addGroup(newName, newUrl);
    if (item) {
      setSelectedId(item.id);
      setNewName('');
      setNewUrl('');
      setShowAdd(false);
      toast({ title: 'Group added', description: `${item.name} is now in your list.` });
    }
  };

  const handleRemove = (id) => {
    const g = groups.find((x) => x.id === id);
    if (!g) return;
    if (window.confirm(`Remove "${g.name}" from your groups?`)) {
      removeGroup(id);
      if (selectedId === id) {
        const fallback = groups.find((x) => x.id !== id);
        setSelectedId(fallback?.id || '');
      }
      toast({ title: 'Group removed' });
    }
  };

  return (
    <section
      className="max-w-7xl mx-auto px-3 sm:px-6 pb-10"
      data-testid="fb-share-section"
    >
      <div
        className="relative rounded-3xl border-[3px] shadow-[0_6px_0_#3a1064] overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #1877f2 0%, #4a6cf7 45%, #9d3dff 100%)',
          borderColor: '#3a1064',
        }}
      >
        {/* Decorative grain */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 1px, transparent 2px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.3) 1px, transparent 2px)',
            backgroundSize: '24px 24px, 32px 32px',
          }}
        />

        <div className="relative p-3 sm:p-6 md:p-8">
          <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
            <div className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-white border-[3px] border-[#3a1064] flex items-center justify-center shrink-0 shadow-[0_4px_0_#3a1064]">
              <Facebook className="h-6 w-6 sm:h-8 sm:w-8 text-[#1877f2]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-black text-lg sm:text-2xl md:text-3xl tracking-tight drop-shadow-[0_2px_0_#3a1064] leading-tight">
                Share with a Facebook Group
              </h2>
              <p className="text-white/90 font-bold text-xs sm:text-sm md:text-base mt-1">
                Pick a Fortnite sprite trading group and post your collection or trade requests.
              </p>
            </div>
          </div>

          <div className="mt-4 sm:mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 sm:gap-4 items-end">
            <div>
              <Label className="text-white font-black text-xs sm:text-sm flex items-center gap-1.5 mb-1.5">
                <ChevronDown className="h-4 w-4" /> Choose a group
              </Label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger
                      data-testid="fb-group-select-trigger"
                      className="h-11 sm:h-12 rounded-xl border-2 border-[#3a1064] bg-white font-black text-[#3a1064] shadow-[0_3px_0_#3a1064] text-sm sm:text-base"
                    >
                      <SelectValue placeholder="Pick a Facebook group..." />
                    </SelectTrigger>
                    <SelectContent
                      className="border-2 border-[#3a1064] rounded-xl bg-white"
                      data-testid="fb-group-select-content"
                    >
                      {groups.length === 0 ? (
                        <div className="px-3 py-6 text-sm font-bold text-[#3a1064]/70 text-center">
                          No groups yet — add one →
                        </div>
                      ) : (
                        groups.map((g) => (
                          <SelectItem
                            key={g.id}
                            value={g.id}
                            className="font-black text-[#3a1064] focus:bg-yellow-100"
                            data-testid={`fb-group-option-${g.id}`}
                          >
                            <span className="flex items-center gap-2">
                              <Facebook className="h-3.5 w-3.5 text-[#1877f2]" />
                              {g.name}
                              {!g.builtIn && (
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-yellow-300 text-[#3a1064]">
                                  Custom
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  data-testid="fb-add-group-toggle-btn"
                  onClick={() => setShowAdd((v) => !v)}
                  className="h-11 sm:h-12 rounded-xl border-2 border-[#3a1064] bg-[#ffd900] hover:bg-yellow-300 text-[#3a1064] font-black shadow-[0_3px_0_#3a1064] px-3 sm:px-4 shrink-0"
                >
                  <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Add</span>
                </Button>
              </div>
              {selected && !selected.builtIn && (
                <button
                  type="button"
                  data-testid="fb-remove-group-btn"
                  onClick={() => handleRemove(selected.id)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-black text-white/90 hover:text-white underline underline-offset-2"
                >
                  <Trash2 className="h-3 w-3" /> Remove this group
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                data-testid="fb-open-group-btn"
                onClick={handleOpenGroup}
                disabled={!selected}
                className="h-11 sm:h-12 rounded-xl border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#1877f2] font-black shadow-[0_3px_0_#3a1064] px-3 sm:px-5 text-xs sm:text-sm flex-1 sm:flex-initial"
              >
                <ExternalLink className="h-4 w-4 mr-1.5 sm:mr-2 shrink-0" /> Open Group
              </Button>
              <Button
                type="button"
                data-testid="fb-share-dialog-btn"
                onClick={handleFacebookShareDialog}
                className="h-11 sm:h-12 rounded-xl border-2 border-[#3a1064] bg-[#1877f2] hover:bg-[#1465d8] text-white font-black shadow-[0_3px_0_#3a1064] px-3 sm:px-5 text-xs sm:text-sm flex-1 sm:flex-initial"
              >
                <Share2 className="h-4 w-4 mr-1.5 sm:mr-2 shrink-0" /> Share to Facebook
              </Button>
              <Button
                type="button"
                data-testid="fb-copy-text-btn"
                onClick={handleCopyOnly}
                variant="outline"
                className="h-11 sm:h-12 rounded-xl border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black shadow-[0_3px_0_#3a1064] px-3 sm:px-4 text-xs sm:text-sm flex-1 sm:flex-initial"
              >
                <Copy className="h-4 w-4 mr-1.5 sm:mr-2 shrink-0" /> Copy text
              </Button>
            </div>
          </div>

          {showAdd && (
            <div
              data-testid="fb-add-group-form"
              className="mt-4 bg-white/95 backdrop-blur rounded-2xl border-2 border-[#3a1064] p-4 shadow-[0_4px_0_#3a1064]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-[#3a1064] font-black text-xs mb-1 block">Group name</Label>
                  <Input
                    data-testid="fb-add-group-name-input"
                    placeholder="e.g. My Sprite Squad"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                  />
                </div>
                <div>
                  <Label className="text-[#3a1064] font-black text-xs mb-1 block">Group URL</Label>
                  <Input
                    data-testid="fb-add-group-url-input"
                    placeholder="https://www.facebook.com/groups/..."
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                  />
                </div>
                <Button
                  type="button"
                  data-testid="fb-add-group-save-btn"
                  onClick={handleAdd}
                  className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064] px-4"
                >
                  <Plus className="h-4 w-4 mr-1" /> Save
                </Button>
              </div>
              <p className="text-[#3a1064]/70 text-[11px] font-bold mt-2">
                Paste the full Facebook group URL (groups must be public for direct linking).
              </p>
            </div>
          )}

          <div className="mt-5 bg-white/15 backdrop-blur-sm rounded-2xl border-2 border-white/40 p-3 sm:p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-white/80 mb-1">Preview of your share</div>
            <pre className="text-white font-bold text-sm whitespace-pre-wrap leading-relaxed font-sans">
{buildShareText()}
            </pre>
          </div>
        </div>
      </div>
    </section>
  );
}
